import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Shield, Clock, MapPin, Wrench, Users, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TradieCard from '@/components/TradieCard';
import { getTradies, getRegions, getCategories } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const HomePage = () => {
  const [tradies, setTradies] = useState([]);
  const [totalTradieCount, setTotalTradieCount] = useState(0);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [regionsData, categoriesData, tradiesData] = await Promise.all([
          getRegions(),
          getCategories(),
          getTradies()
        ]);
        setRegions(regionsData);
        setCategories(categoriesData);
        setTotalTradieCount(tradiesData.length);
        setTradies(tradiesData.slice(0, 6)); // Show first 6 featured tradies
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Calculate display count: minimum 10+, then round down to nearest 10
  const getDisplayTradieCount = () => {
    if (totalTradieCount === 0) return '—';
    const rounded = Math.floor(totalTradieCount / 10) * 10;
    const displayNum = Math.max(10, rounded);
    return `${displayNum}+`;
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedRegion) params.append('region', selectedRegion);
    if (selectedCategory) params.append('category', selectedCategory);
    trackEvent('homepage_search', {
      region: selectedRegion || 'any',
      category: selectedCategory || 'any',
    });
    window.location.href = `/directory${params.toString() ? '?' + params.toString() : ''}`;
  };

  const features = [
    {
      icon: Shield,
      title: 'Verified Tradies',
      description: 'All listed tradies are reviewed and approved before listing'
    },
    {
      icon: Clock,
      title: 'Quick Response',
      description: 'Send enquiries directly to tradies for fast quotes'
    },
    {
      icon: MapPin,
      title: 'Local Experts',
      description: 'Find tradies in your region across New Zealand'
    }
  ];

  const serviceHighlights = [
    { icon: Wrench, label: 'Insulation' },
    { icon: Wrench, label: 'Heating' },
    { icon: Wrench, label: 'Ventilation' },
    { icon: Wrench, label: 'Moisture Control' },
    { icon: Wrench, label: 'Draught Stopping' },
    { icon: Wrench, label: 'Building Maintenance' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-stone-200 shadow-sm text-primary text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4 text-secondary" />
                Trusted by New Zealand homeowners
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1] mb-6">
                Find Qualified{' '}
                <span className="text-primary">Healthy Homes</span>{' '}
                Tradies
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg mb-8">
                Connect with verified professionals for insulation, heating, ventilation, and all your healthy homes compliance needs across New Zealand.
              </p>
              
              {/* Search Box */}
              <div className="bg-white rounded-xl shadow-lg p-4 border border-stone-200" data-testid="search-box">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                    <SelectTrigger data-testid="region-select">
                      <SelectValue placeholder="Select Region" />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.map((region) => (
                        <SelectItem key={region} value={region}>
                          {region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select Service" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    onClick={handleSearch}
                    className="bg-primary hover:bg-primary/90 h-10"
                    data-testid="search-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex items-center gap-8 mt-8">
                <div>
                  <p className="font-heading text-3xl font-bold text-primary">{getDisplayTradieCount()}</p>
                  <p className="text-sm text-muted-foreground">Verified Tradies</p>
                </div>
                <div className="w-px h-12 bg-stone-200" />
                <div>
                  <p className="font-heading text-3xl font-bold text-primary">16</p>
                  <p className="text-sm text-muted-foreground">NZ Regions</p>
                </div>
                <div className="w-px h-12 bg-stone-200" />
                <div>
                  <p className="font-heading text-3xl font-bold text-primary">9</p>
                  <p className="text-sm text-muted-foreground">Service Types</p>
                </div>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative animate-fade-in stagger-2 hidden lg:block">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_c191cfdf-91ed-4cf4-87f2-18641b22d95c/artifacts/tyy23fhs_maintenance_home-preview_hires.jpg"
                  alt="A New Zealand tradie maintaining a home — pressure-washing the roof and gutters"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-foreground">Local Experts</p>
                    <p className="text-sm text-muted-foreground">Across NZ</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`p-8 rounded-2xl bg-stone-50 hover:bg-white hover:shadow-lg hover:border-stone-200 border border-transparent transition-all duration-300 animate-fade-in stagger-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-xl mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
              Healthy Homes Services
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Find tradies specialising in all aspects of healthy homes compliance and maintenance
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {serviceHighlights.map((service) => (
              <Link
                key={service.label}
                to={`/directory?category=${encodeURIComponent(service.label)}`}
                className="p-4 rounded-xl bg-white border border-stone-200 hover:border-primary/30 hover:shadow-md transition-all duration-300 text-center group"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
                <p className="font-medium text-sm">{service.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Tradies Section */}
      {tradies.length > 0 && (
        <section className="py-16 bg-white" data-testid="featured-tradies">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-2">
                  Featured Tradies
                </h2>
                <p className="text-muted-foreground">
                  Discover verified professionals ready to help
                </p>
              </div>
              <Link to="/directory">
                <Button variant="outline" className="gap-2" data-testid="view-all-btn">
                  View All
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tradies.map((tradie) => (
                <TradieCard key={tradie.id} tradie={tradie} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Are You a Tradie?
          </h2>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
            Join our directory and connect with homeowners looking for healthy homes services. 
            Monthly subscription for $49 NZD.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-stone-100 font-semibold px-8"
              data-testid="cta-register-btn"
            >
              List Your Business
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
